import React from "react";
import { NavLink } from "react-router-dom";
import {
  HomeIcon,
  UsersIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const { logout, adminUser } = useAuth();
  const currentRole = adminUser?.role ?? null;

  const navigation = [
    { name: "Дашборд", href: "/admin", icon: HomeIcon, allowed: true },
    {
      name: "Пользователи",
      href: "/admin/users",
      icon: UsersIcon,
      allowed: currentRole === "superadmin",
    },
    {
      name: "Биллинг",
      href: "/admin/billing",
      icon: CogIcon,
      allowed: currentRole === "superadmin",
    },
    {
      name: "Настройки",
      href: "/admin/settings",
      icon: CogIcon,
      allowed: true,
    },
  ].filter((i) => i.allowed);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/60 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed inset-y-0 left-0 z-50 ${isCollapsed ? "w-20" : "w-72"} bg-gradient-to-b from-primary-800 to-primary-900 text-white shadow-2xl ring-1 ring-black/10 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${isOpen ? "translate-x-0" : "-translate-x-full"}
      `}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div
            className={`flex h-16 items-center justify-between ${isCollapsed ? "px-2" : "px-4"} border-b border-white/10`}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-9 w-9 bg-white/10 backdrop-blur rounded-lg flex items-center justify-center ring-1 ring-white/20">
                  <svg
                    className="h-5 w-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
              {!isCollapsed && (
                <div className="ml-3">
                  <h1 className="text-lg font-semibold text-white">
                    Admin Panel
                  </h1>
                  <p className="text-xs text-white/70">RevuAI Assistant</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              {/* Collapse toggle (desktop) */}
              <button
                type="button"
                onClick={onToggleCollapse}
                className="hidden lg:inline-flex p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10"
                title={isCollapsed ? "Развернуть" : "Свернуть"}
              >
                {isCollapsed ? (
                  <ChevronRightIcon className="h-4 w-4" />
                ) : (
                  <ChevronLeftIcon className="h-4 w-4" />
                )}
              </button>
              {/* Close (mobile) */}
              <button
                onClick={onClose}
                className="lg:hidden p-1 rounded-md text-white/70 hover:text-white hover:bg-white/10"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav
            className={`flex-1 ${isCollapsed ? "px-2" : "px-4"} py-6 space-y-2`}
          >
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === "/admin"}
                className={({ isActive }) => `
                  group relative flex items-center ${isCollapsed ? "px-3 justify-center" : "px-4"} py-2 text-sm font-medium rounded-xl transition-all duration-200
                  ${
                    isActive
                      ? "bg-white/10 text-white shadow-inner ring-1 ring-white/10"
                      : "text-white/80 hover:text-white hover:bg-white/5"
                  }
                `}
                onClick={onClose}
              >
                <item.icon
                  className={`${isCollapsed ? "mr-0" : "mr-3"} h-5 w-5`}
                />
                {!isCollapsed && item.name}
                {isCollapsed && (
                  <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 whitespace-nowrap rounded-md bg-gray-900/90 px-2 py-1 text-xs text-white opacity-0 shadow-lg ring-1 ring-black/10 transition-opacity duration-150 group-hover:opacity-100">
                    {item.name}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Footer */}
          <div
            className={`border-t border-white/10 ${isCollapsed ? "p-3" : "p-4"}`}
          >
            <button
              onClick={logout}
              className={`group flex items-center w-full ${isCollapsed ? "justify-center" : ""} px-3 py-2 text-sm font-medium text-white/80 rounded-lg hover:bg-white/5 hover:text-white transition-colors duration-200`}
            >
              <ArrowRightOnRectangleIcon
                className={`${isCollapsed ? "mr-0" : "mr-3"} h-5 w-5`}
              />
              {!isCollapsed && "Выйти"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
